import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
// O contexto que as superfícies compartilhadas (anúncios, consentimento) leem:
// este provider o alimenta com a mesma sessão do portal.
import { AuthContext } from '@/lib/auth/context';
import type { AuthContextValue, AuthStatus } from '@/lib/auth/types';
import { USE_MOCKS } from '../api';
import { MOCK_ADMIN_TOKEN } from '../api/mock-client';
import { PortalAuthError } from './cognito-errors';
import {
  getAccessToken as currentAccessToken,
  restoreMember,
  signInWithGoogle as startGoogle,
  signInWithPassword as authenticate,
  signOut as endSession,
  updateDisplayName as saveDisplayName,
} from './provider';
import type { BlogMember } from './session';

export interface BlogAuthContextValue {
  status: AuthStatus;
  /** O membro logado no portal (null para o visitante). */
  user: BlogMember | null;
  /** Token fresco da sessão do portal — o Bearer da Members API. */
  getAccessToken: () => Promise<string | null>;
  /**
   * Login nativo (SRP) com e-mail e senha. Resolve com a sessão gravada e o
   * membro no contexto; rejeita com {@link ./cognito-errors PortalAuthError}.
   */
  signInWithPassword: (email: string, password: string) => Promise<void>;
  /** Hosted UI direto no consentimento do Google (sai da página). */
  signInWithGoogle: (returnPath?: string) => Promise<void>;
  /**
   * Entrada de demonstração — SÓ no modo offline (VITE_USE_MOCKS): cria a
   * sessão local do membro de demonstração (ou, com `asAdmin`, a do admin) para
   * a área logada ser exercitável sem provedor. Fora do mock, lança.
   */
  signInWithMock: (asAdmin?: boolean) => void;
  /**
   * Troca o nome de exibição (1 a 80 caracteres, aparado) e atualiza o membro
   * no contexto. Rejeita com {@link PortalAuthError} (inclusive no Cognito de
   * rollback, que não tem a troca).
   */
  updateDisplayName: (name: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const BlogAuthContext = createContext<BlogAuthContextValue | null>(null);

const MOCK_SESSION_KEY = 'seesforce.blogMockSession';
const MOCK_TOKEN = 'blog-mock-token';
/** O valor da chave do mock: membro comum ou admin de demonstração. */
const MOCK_MEMBER_SESSION = 'active';
const MOCK_ADMIN_SESSION = 'admin';

const MOCK_MEMBER: BlogMember = {
  id: 'mem-0001',
  email: 'aluna@exemplo.com.br',
  displayName: 'Aluna Demo',
  isAdmin: false,
};

const MOCK_ADMIN: BlogMember = {
  id: 'mem-0002',
  email: 'admin@exemplo.com.br',
  displayName: 'Admin Demo',
  isAdmin: true,
};

const DISPLAY_NAME_MAX = 80;

function readMockMember(): BlogMember | null {
  try {
    const session = localStorage.getItem(MOCK_SESSION_KEY);
    if (session === MOCK_ADMIN_SESSION) return MOCK_ADMIN;
    return session === MOCK_MEMBER_SESSION ? MOCK_MEMBER : null;
  } catch {
    return null;
  }
}

/**
 * A sessão do portal. Fora do mock ela vem do provedor configurado
 * ({@link ./provider}: Supabase ou o Cognito de rollback), aberta pelo login da
 * página /login ou pelo Google; no mock, a entrada de demonstração acima. O
 * provider também alimenta o AuthContext compartilhado (`lib/auth/context`) com
 * a mesma sessão — os componentes editoriais só leem `status`.
 */
export function BlogAuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [user, setUser] = useState<BlogMember | null>(null);

  useEffect(() => {
    if (USE_MOCKS) {
      // Offline o portal nasce VISITANTE — o blog é majoritariamente anônimo, e
      // a área logada se alcança pelo botão de demonstração da tela de login.
      const restored = readMockMember();
      setUser(restored);
      setStatus(restored ? 'authenticated' : 'unauthenticated');
      return;
    }

    let cancelled = false;
    void (async () => {
      try {
        const restored = await restoreMember();
        if (cancelled) return;
        setUser(restored);
        setStatus(restored ? 'authenticated' : 'unauthenticated');
      } catch {
        if (cancelled) return;
        setUser(null);
        setStatus('unauthenticated');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const getAccessToken = useCallback(async (): Promise<string | null> => {
    if (USE_MOCKS) {
      const member = readMockMember();
      if (member === null) return null;
      return member.isAdmin ? MOCK_ADMIN_TOKEN : MOCK_TOKEN;
    }
    return currentAccessToken();
  }, []);

  const signInWithPassword = useCallback(async (email: string, password: string): Promise<void> => {
    // O SDK do provedor desce sob demanda, com a tela de login — não com o blog.
    const member = await authenticate(email, password);
    setUser(member);
    setStatus('authenticated');
  }, []);

  const signInWithGoogle = useCallback(async (returnPath?: string): Promise<void> => {
    await startGoogle(returnPath);
  }, []);

  const signInWithMock = useCallback((asAdmin = false): void => {
    if (!USE_MOCKS) {
      throw new Error('A entrada de demonstração só existe no modo offline.');
    }
    try {
      localStorage.setItem(MOCK_SESSION_KEY, asAdmin ? MOCK_ADMIN_SESSION : MOCK_MEMBER_SESSION);
    } catch {
      /* sem storage a sessão vale nesta carga da página */
    }
    setUser(asAdmin ? MOCK_ADMIN : MOCK_MEMBER);
    setStatus('authenticated');
  }, []);

  const updateDisplayName = useCallback(async (name: string): Promise<void> => {
    const trimmed = name.trim();
    const length = Array.from(trimmed).length;
    if (length < 1 || length > DISPLAY_NAME_MAX) {
      throw new PortalAuthError(
        'InvalidName',
        `O nome precisa ter de 1 a ${DISPLAY_NAME_MAX} caracteres.`,
      );
    }
    if (USE_MOCKS) {
      setUser((current) => (current ? { ...current, displayName: trimmed } : current));
      return;
    }
    // O contexto não relê a sessão sozinho: o membro novo vem da resposta.
    setUser(await saveDisplayName(trimmed));
  }, []);

  const signOut = useCallback(async (): Promise<void> => {
    if (USE_MOCKS) {
      try {
        localStorage.removeItem(MOCK_SESSION_KEY);
      } catch {
        /* nada a limpar */
      }
    } else {
      // Limpar só o storage deixaria o refresh vivo no provedor (e, na entrada
      // pelo Google, o cookie do Hosted UI): o helper revoga e encerra os dois.
      await endSession();
    }
    setUser(null);
    setStatus('unauthenticated');
  }, []);

  const value = useMemo<BlogAuthContextValue>(
    () => ({
      status,
      user,
      getAccessToken,
      signInWithPassword,
      signInWithGoogle,
      signInWithMock,
      updateDisplayName,
      signOut,
    }),
    [
      status,
      user,
      getAccessToken,
      signInWithPassword,
      signInWithGoogle,
      signInWithMock,
      updateDisplayName,
      signOut,
    ],
  );

  // O contexto compartilhado (`lib/auth/context`): só `status` é lido pelos
  // componentes editoriais (anúncios somem para o leitor logado; o aviso de
  // cookies espera a sessão resolver). É a mesma sessão, na forma que eles leem.
  const platformValue = useMemo<AuthContextValue>(
    () => ({ status, user, getAccessToken, signOut }),
    [status, user, getAccessToken, signOut],
  );

  return (
    <AuthContext.Provider value={platformValue}>
      <BlogAuthContext.Provider value={value}>{children}</BlogAuthContext.Provider>
    </AuthContext.Provider>
  );
}

export function useBlogAuth(): BlogAuthContextValue {
  const ctx = useContext(BlogAuthContext);
  if (!ctx) {
    throw new Error('useBlogAuth must be used within a BlogAuthProvider.');
  }
  return ctx;
}

/**
 * A sessão, ou null fora do provider — para o chrome do site (topbar) renderizar
 * como visitante em testes e superfícies montadas sem a sessão.
 */
export function useBlogAuthOptional(): BlogAuthContextValue | null {
  return useContext(BlogAuthContext);
}
